/**
 * Sign-in handoff from the POS app (ppos.my / pharmapos.com).
 *
 * The storefront lives on its own origin (mall.ppos.my, mall.pharmapos.com),
 * so Firebase's own persistence cannot carry a login across. The POS app
 * therefore redirects with `#sso=<gateway session key>&email=<email>`; the
 * mall keeps that for the current tab and sends it as `X-Session-Key` on
 * authenticated calls — the same credential `/api/auth/login` issues.
 *
 * The fragment is never sent to a server and is scrubbed from the address bar
 * as soon as it is read. The session key stays in sessionStorage, so it dies
 * with the tab (and a mall "Sign out" only forgets it here — it must NOT call
 * /auth/logout, which would kill the POS session too).
 */

export interface HandoffSession {
  sessionKey: string;
  email: string;
}

const STORAGE_KEY = 'ppos_mall_sso';

let session: HandoffSession | null = null;
let restored = false;

function restore(): HandoffSession | null {
  if (restored) return session;
  restored = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.sessionKey === 'string' && parsed.sessionKey) {
      session = { sessionKey: parsed.sessionKey, email: String(parsed.email || '') };
    }
  } catch {
    /* corrupt entry / storage unavailable — stay signed out */
  }
  return session;
}

/** Currently held handoff session (survives reloads in this tab). */
export function handoffSession(): HandoffSession | null {
  return restore();
}

/** Session key to send as `X-Session-Key`, or null. */
export function handoffSessionKey(): string | null {
  return restore()?.sessionKey ?? null;
}

export function setHandoffSession(next: HandoffSession | null): void {
  session = next;
  restored = true;
  try {
    if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / quota — the in-memory copy still works */
  }
}

/**
 * Read a handoff out of the URL fragment, remember it, and clean the URL.
 * Returns null when the page was not opened with a handoff.
 */
export function consumeHandoffFragment(): HandoffSession | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const sessionKey = params.get('sso') || '';
  if (!sessionKey) return null;

  // Drop the credential from the address bar + history before doing anything
  // else, so it cannot leak through a shared link or a reload.
  window.history.replaceState({}, '', window.location.pathname + window.location.search);

  const next: HandoffSession = { sessionKey, email: params.get('email') || '' };
  setHandoffSession(next);
  return next;
}
